<script setup lang="ts">
import { faPlane } from '@fortawesome/pro-regular-svg-icons';
import RouteView from './RouteView.vue';
import type TripSegment from '@itinerary/trip/TripSegment';
import { VehicleType } from '@itinerary/trip/types';
import { busSide, taxiSide, trainSide } from '@static/customIcons';

interface IProps {
    segment: TripSegment;
    showDuration?: boolean;
    fullWidth?: boolean;
}
const { segment, showDuration, fullWidth } = defineProps<IProps>();

const duration = computed(() => formatTripDuration(segment.duration * 1000));
const stops = computed(() =>
    segment.trips.length > 1 ? segment.trips.length - 1 : 0
);

const numberOFDomesticStops = computed(() =>
    segment.trips.reduce((prev, next) => prev + next.stops, 0)
);

const icon = computed(() => {
    switch (segment.trips[0].vehicleType) {
        case VehicleType.Plane:
            return {
                icon: faPlane,
                rotate: '180' as const,
            };
        case VehicleType.Train:
            return {
                icon: trainSide,
            };
        case VehicleType.Bus:
            return {
                icon: busSide,
            };
        case VehicleType.Taxi:
            return {
                icon: taxiSide,
            };
        default:
            return {
                icon: undefined,
            };
    }
});

const allStops = computed(() => numberOFDomesticStops.value + stops.value);
</script>

<template>
    <div
        class="route-view-container"
        :style="{ maxWidth: fullWidth ? 'unset' : '150px' }"
    >
        <div v-if="showDuration" class="duration">
            {{ duration }}
        </div>
        <RouteView :stops="allStops" :icon="icon.icon" :rotate="icon.rotate" />
    </div>
</template>

<style lang="scss" scoped>
.route-view-container {
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 32px;
    @include mobile-only {
        flex: 1;
        width: auto;
    }
    .duration {
        position: absolute;
        top: -18px;
        @include tg-s($w5, usePalette($p-cool-gray, $p-500));
        text-align: center;
    }
}
</style>
